import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Download, LogOut, Check, X, Trash2, User, Mail, Phone, GraduationCap, Calendar, FileText, Upload, Camera, DollarSign, Eye, EyeOff, Plus, School, ImageIcon, Edit, Save, Star, MessageSquare, Shield, ShieldCheck, ChevronUp, ChevronDown, Pencil } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [bookings, setBookings] = useState([]);
  const [guideApplications, setGuideApplications] = useState([]);
  const [tourBookings, setTourBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('bookings');
  const [editingPhotosId, setEditingPhotosId] = useState(null);
  const [newPhotos, setNewPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [settingProfilePic, setSettingProfilePic] = useState(null);
  const [editingAvailabilityId, setEditingAvailabilityId] = useState(null);
  const [availabilityDisplay, setAvailabilityDisplay] = useState('');
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(null);
  // Pricing state
  const [editingPricingId, setEditingPricingId] = useState(null);
  const [pricingCampus, setPricingCampus] = useState('');
  const [pricingVideo, setPricingVideo] = useState('');
  const [savingPricing, setSavingPricing] = useState(false);
  const [calculatedPrices, setCalculatedPrices] = useState({ campus: null, video: null });
  // Schools state
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [showAddSchool, setShowAddSchool] = useState(false);
  const [newSchool, setNewSchool] = useState({ name: '', city: '', image: '' });
  const [addingSchool, setAddingSchool] = useState(false);
  // Photo position state
  const [savingPhotoPosition, setSavingPhotoPosition] = useState(null);
  // Edit guide state
  const [editingGuideId, setEditingGuideId] = useState(null);
  const [editGuideData, setEditGuideData] = useState({});
  const [savingGuide, setSavingGuide] = useState(false);
  // Edit school state
  const [editingSchoolId, setEditingSchoolId] = useState(null);
  const [editSchoolData, setEditSchoolData] = useState({ name: '', city: '', image: '' });
  const [savingSchool, setSavingSchool] = useState(false);
  const fileInputRef = useRef(null);
  const schoolImageInputRef = useRef(null);
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const { toast } = useToast();
  
  const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
      });
      
      if (!response.ok) {
        throw new Error('Invalid credentials');
      }
      
      const data = await response.json();
      localStorage.setItem('admin_token', data.token);
      setIsLoggedIn(true);
      
      toast({
        title: "Login Successful",
        description: "Welcome to the admin dashboard"
      });
      
      // Load data
      loadBookings(data.token);
      loadGuideApplications(data.token);
      loadSchools(data.token);
      loadReviews(data.token);
      loadTourBookings(data.token);
      
    } catch (error) {
      toast({
        title: "Login Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadBookings = async (token) => {
    try {
      const response = await fetch(`${API}/api/bookings`, {
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('admin_token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load bookings');
      }
      
      const data = await response.json();
      setBookings(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load bookings",
        variant: "destructive"
      });
    }
  };

  const [editingReviewId, setEditingReviewId] = useState(null);
  const [editReviewerName, setEditReviewerName] = useState('');

  const handleSaveReviewerName = async (reviewId) => {
    try {
      const response = await fetch(`${API}/api/admin/reviews/${reviewId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reviewer_name: editReviewerName })
      });
      if (response.ok) {
        toast({ title: "Updated", description: "Reviewer name updated" });
        loadReviews();
      } else {
        toast({ title: "Error", description: "Failed to update", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
    setEditingReviewId(null);
  };

  const loadTourBookings = async (token) => {
    try {
      const response = await fetch(`${API}/api/admin/guide-bookings`, {
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('admin_token')}`
        }
      });
      if (!response.ok) throw new Error('Failed to load tour bookings');
      const data = await response.json();
      setTourBookings(data);
    } catch (error) {
      console.error('Error loading tour bookings:', error);
    }
  };

  const handleRefundDeposit = async (bookingId) => {
    if (!window.confirm('Are you sure you want to refund this deposit? This cannot be undone.')) return;
    try {
      const response = await fetch(`${API}/api/admin/refund/${bookingId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast({ title: "Refund Processed", description: `Refund ID: ${data.refund_id}` });
        loadTourBookings();
      } else {
        toast({ title: "Refund Failed", description: data.detail || "Unknown error", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleChargeBalance = async (bookingId, tipAmount = 0) => {
    const tip = prompt('Enter tip amount (or 0 for no tip):', tipAmount || '0');
    if (tip === null) return;
    const parsedTip = parseFloat(tip) || 0;
    if (!window.confirm(`Charge remaining balance + $${parsedTip.toFixed(2)} tip to saved card?`)) return;
    try {
      const response = await fetch(`${API}/api/admin/charge-balance/${bookingId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tip_amount: parsedTip })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast({ title: "Balance Charged", description: `Charged $${data.amount_charged.toFixed(2)} (tip: $${data.tip.toFixed(2)})` });
        loadTourBookings();
      } else {
        toast({ title: "Charge Failed", description: data.detail || "Card may have been declined", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleSendTipLink = async (booking) => {
    const tipUrl = `${window.location.origin}/tip/${booking.id}`;
    try {
      const response = await fetch(`${API}/api/admin/test-email/${booking.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      toast({ title: "Tip Link", description: `Share this link with ${booking.visitor_name}: ${tipUrl}` });
      await navigator.clipboard.writeText(tipUrl);
      toast({ title: "Copied!", description: "Tip link copied to clipboard" });
    } catch (error) {
      toast({ title: "Tip Link", description: tipUrl });
    }
  };

  const loadGuideApplications = async (token) => {
    try {
      const response = await fetch(`${API}/api/admin/guide-applications`, {
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('admin_token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load guide applications');
      }
      
      const data = await response.json();
      setGuideApplications(data);
    } catch (error) {
      console.error('Failed to load guide applications:', error);
    }
  };

  const handleApproveGuide = async (applicationId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/guide-applications/${applicationId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to approve application');
      }
      
      toast({
        title: "Application Approved",
        description: "The guide has been approved and is now visible to the public"
      });
      
      loadGuideApplications(token);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleRejectGuide = async (applicationId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/guide-applications/${applicationId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to reject application');
      }
      
      toast({
        title: "Application Rejected",
        description: "The guide application has been rejected"
      });
      
      loadGuideApplications(token);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleDeleteGuide = async (applicationId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/guide-applications/${applicationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete application');
      }
      
      toast({
        title: "Application Deleted",
        description: "The guide application has been removed"
      });
      
      loadGuideApplications(token);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handlePhotoFileChange = (e) => {
    const files = Array.from(e.target.files);
    setNewPhotos(files);
  };

  const handleAddPhotos = async (applicationId) => {
    if (newPhotos.length < 1) {
      toast({
        title: "No photos selected",
        description: "Please select at least 1 photo to add",
        variant: "destructive"
      });
      return;
    }

    setUploadingPhotos(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      newPhotos.forEach(photo => {
        formData.append('photos', photo);
      });

      const response = await fetch(`${API}/api/admin/guide-applications/${applicationId}/add-photos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to add photos');
      }

      toast({
        title: "Photos Added",
        description: "New photos have been added successfully"
      });

      setEditingPhotosId(null);
      setNewPhotos([]);
      loadGuideApplications(token);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleDeletePhoto = async (applicationId, photoIndex) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    setDeletingPhoto(`${applicationId}-${photoIndex}`);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/guide-applications/${applicationId}/delete-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ photo_index: photoIndex })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete photo');
      }

      toast({
        title: "Photo Deleted",
        description: "The photo has been removed"
      });

      loadGuideApplications(token);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setDeletingPhoto(null);
    }
  };

  const handleSetProfilePicture = async (applicationId, photoIndex) => {
    setSettingProfilePic(`${applicationId}-${photoIndex}`);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/guide-applications/${applicationId}/set-profile-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ photo_index: photoIndex })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to set profile picture');
      }

      toast({
        title: "Profile Picture Updated",
        description: "The profile picture has been changed successfully"
      });

      loadGuideApplications(token);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSettingProfilePic(null);
    }
  };

  const handleSaveAvailabilityDisplay = async (applicationId) => {
    setSavingAvailability(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/guide-applications/${applicationId}/update-availability-display`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ availability_display: availabilityDisplay })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update availability');
      }

      toast({
        title: "Availability Updated",
        description: "The availability display has been updated"
      });

      setEditingAvailabilityId(null);
      setAvailabilityDisplay('');
      loadGuideApplications(token);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSavingAvailability(false);
    }
  };

  // Pricing mode state
  const [pricingMode, setPricingMode] = useState('percentage'); // 'percentage' or 'fixed'
  const [fixedFee, setFixedFee] = useState(50); // Default $50 fixed fee

  // Calculate customer price from guide's desired earnings
  const calculateCustomerPrice = (guideEarnings) => {
    if (!guideEarnings || guideEarnings <= 0) return null;
    if (pricingMode === 'fixed') {
      // Fixed fee: customer pays guide earnings + fixed fee
      return parseFloat(guideEarnings) + fixedFee;
    } else {
      // Percentage: 20% commission
      const commissionRate = 0.20;
      const customerPrice = parseFloat(guideEarnings) / (1 - commissionRate);
      return Math.round(customerPrice * 100) / 100;
    }
  };

  // Update calculated prices when inputs change
  const handlePricingInputChange = (type, value) => {
    if (type === 'campus') {
      setPricingCampus(value);
      setCalculatedPrices(prev => ({
        ...prev,
        campus: calculateCustomerPrice(value)
      }));
    } else {
      setPricingVideo(value);
      setCalculatedPrices(prev => ({
        ...prev,
        video: calculateCustomerPrice(value)
      }));
    }
  };

  // Recalculate when pricing mode or fixed fee changes
  useEffect(() => {
    if (pricingCampus) {
      setCalculatedPrices(prev => ({
        ...prev,
        campus: calculateCustomerPrice(pricingCampus)
      }));
    }
    if (pricingVideo) {
      setCalculatedPrices(prev => ({
        ...prev,
        video: calculateCustomerPrice(pricingVideo)
      }));
    }
  }, [pricingMode, fixedFee]);

  const handleSavePricing = async (applicationId) => {
    setSavingPricing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/guide-applications/${applicationId}/update-pricing`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          desired_earnings_campus: pricingCampus ? parseFloat(pricingCampus) : null,
          desired_earnings_video: pricingVideo ? parseFloat(pricingVideo) : null
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update pricing');
      }

      const result = await response.json();
      
      toast({
        title: "Pricing Updated",
        description: `Prices saved! Campus: $${result.pricing.price_campus_tour || 'N/A'}, Video: $${result.pricing.price_video_chat || 'N/A'}`
      });

      setEditingPricingId(null);
      setPricingCampus('');
      setPricingVideo('');
      setCalculatedPrices({ campus: null, video: null });
      loadGuideApplications(token);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSavingPricing(false);
    }
  };

  // ============= Edit Guide Functions =============
  
  const startEditingGuide = (application) => {
    setEditingGuideId(application.id);
    setEditGuideData({
      name: application.name || '',
      email: application.email || '',
      phone: application.phone || '',
      school: application.school || '',
      bio: application.bio || '',
      academic_year: application.academic_year || '',
      major: application.major || '',
      minor: application.minor || '',
      hometown: application.hometown || '',
      high_school: application.high_school || '',
      college_experience: application.college_experience || '',
      tip_for_students: application.tip_for_students || '',
      favorite_class: application.favorite_class || '',
      career_goals: application.career_goals || '',
      tour_description: application.tour_description || '',
      meeting_point: application.meeting_point || '',
      availability: application.availability || '',
      gender: application.gender || ''
    });
  };

  const handleEditGuideChange = (field, value) => {
    setEditGuideData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveGuide = async (applicationId) => {
    setSavingGuide(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/guide-applications/${applicationId}/edit`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editGuideData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update guide');
      }

      toast({
        title: "Guide Updated",
        description: "Guide information has been saved successfully."
      });

      setEditingGuideId(null);
      setEditGuideData({});
      loadGuideApplications(token);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSavingGuide(false);
    }
  };

  // ============= School Management Functions =============
  
  const loadSchools = async (token) => {
    setLoadingSchools(true);
    try {
      const response = await fetch(`${API}/api/admin/schools`, {
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('admin_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSchools(data);
      }
    } catch (error) {
      console.error('Failed to load schools:', error);
    } finally {
      setLoadingSchools(false);
    }
  };

  const handleToggleSchoolVisibility = async (schoolId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/schools/${schoolId}/toggle-visibility`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to toggle visibility');
      }

      const result = await response.json();
      
      // Update local state
      setSchools(prev => prev.map(school => 
        school.id === schoolId 
          ? { ...school, is_visible: result.is_visible }
          : school
      ));

      toast({
        title: result.is_visible ? "School Visible" : "School Hidden",
        description: result.is_visible 
          ? "School will now appear in Explore Schools" 
          : "School is now hidden from Explore Schools"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleAddSchool = async () => {
    if (!newSchool.name || !newSchool.city) {
      toast({
        title: "Missing Information",
        description: "Please provide school name and city",
        variant: "destructive"
      });
      return;
    }

    setAddingSchool(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/schools`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSchool)
      });

      if (!response.ok) {
        throw new Error('Failed to add school');
      }

      toast({
        title: "School Added",
        description: `${newSchool.name} has been added to the list`
      });

      setNewSchool({ name: '', city: '', image: '' });
      setShowAddSchool(false);
      loadSchools();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setAddingSchool(false);
    }
  };

  const handleDeleteSchool = async (schoolId, schoolName) => {
    if (!window.confirm(`Are you sure you want to delete ${schoolName}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/schools/${schoolId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete school');
      }

      toast({
        title: "School Deleted",
        description: `${schoolName} has been removed`
      });

      loadSchools();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Edit school functions
  const startEditingSchool = (school) => {
    setEditingSchoolId(school.id);
    setEditSchoolData({
      name: school.name || '',
      city: school.city || '',
      image: school.image || ''
    });
  };

  const handleSchoolImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    toast({ title: "Uploading...", description: "Compressing and uploading image" });

    // Compress image client-side before upload
    const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
      return new Promise((resolve) => {
        const img = new Image();
        const canvas = document.createElement('canvas');
        const reader = new FileReader();
        
        reader.onload = (e) => {
          img.onload = () => {
            let width = img.width;
            let height = img.height;
            
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
            
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', quality));
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      });
    };

    try {
      const base64 = await compressImage(file);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ image: base64, filename: file.name })
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const result = await response.json();
      const imageUrl = result.url.startsWith('/') ? `${API}${result.url}` : result.url;
      setEditSchoolData(prev => ({ ...prev, image: imageUrl }));
      
      toast({
        title: "Image Uploaded",
        description: "School image uploaded successfully"
      });
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: "Image may be too large. Try a smaller file.",
        variant: "destructive"
      });
    }
  };

  const handleSaveSchool = async (schoolId) => {
    setSavingSchool(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/schools/${schoolId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editSchoolData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update school');
      }

      toast({
        title: "School Updated",
        description: "School information has been saved successfully."
      });

      setEditingSchoolId(null);
      setEditSchoolData({ name: '', city: '', image: '' });
      loadSchools();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSavingSchool(false);
    }
  };

  // Photo position handler
  const handlePhotoPositionChange = async (applicationId, position) => {
    setSavingPhotoPosition(applicationId);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/guide-applications/${applicationId}/photo-position`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ position })
      });

      if (!response.ok) {
        throw new Error('Failed to update photo position');
      }

      toast({
        title: "Photo Position Updated",
        description: `Photo will now show from the ${position}`
      });

      // Update local state
      setGuideApplications(prev => prev.map(app => 
        app.id === applicationId ? { ...app, photo_position: position } : app
      ));
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSavingPhotoPosition(null);
    }
  };

  // ============= Reviews Management =============
  const handleReorderSchool = async (schoolId, direction) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/schools/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ school_id: schoolId, direction })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.schools) setSchools(data.schools);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to reorder", variant: "destructive" });
    }
  };

  const loadReviews = async (tokenOverride) => {
    setLoadingReviews(true);
    try {
      const token = tokenOverride || localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/reviews`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (error) {
      console.error('Failed to load reviews:', error);
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleToggleReviewVisibility = async (reviewId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/reviews/${reviewId}/toggle-visibility`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, is_visible: result.is_visible } : r));
        toast({ title: result.is_visible ? "Review Visible" : "Review Hidden", description: `Review visibility updated` });
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleVerifyReview = async (reviewId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/reviews/${reviewId}/verify`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, is_verified: true } : r));
        toast({ title: "Review Verified", description: "Review marked as verified" });
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/admin/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setReviews(prev => prev.filter(r => r.id !== reviewId));
        toast({ title: "Review Deleted", description: "Review removed successfully" });
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/api/bookings/export/excel`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bookings_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "Export Successful",
        description: "Bookings exported to Excel"
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsLoggedIn(false);
    setBookings([]);
    setGuideApplications([]);
    setUsername('');
    setPassword('');
  };

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      setIsLoggedIn(true);
      loadBookings(token);
      loadGuideApplications(token);
      loadSchools(token);
      loadReviews(token);
    }
  }, []);

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      confirmed: 'bg-green-100 text-green-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Admin Login</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  placeholder="Enter username"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter password"
                  className="mt-1"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-black text-white hover:bg-gray-800"
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Email Notice Banner */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Manual Email Mode:</strong> Automated emails are temporarily disabled. Click "Email Student" button to send booking confirmations manually.
              </p>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Admin Dashboard</h1>
              <p className="text-gray-600">Manage bookings and guide applications</p>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={handleExportExcel}
                className="bg-green-600 text-white hover:bg-green-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Export to Excel
              </Button>
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="border-2 border-red-600 text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs - Large and Visible */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('bookings')}
              className={`flex-1 py-4 px-6 rounded-lg text-lg font-semibold transition-all ${
                activeTab === 'bookings'
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-300'
              }`}
            >
              Bookings ({bookings.length})
            </button>
            <button
              onClick={() => { setActiveTab('tours'); loadTourBookings(); }}
              className={`flex-1 py-4 px-6 rounded-lg text-lg font-semibold transition-all ${
                activeTab === 'tours'
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-300'
              }`}
            >
              Tour Bookings ({tourBookings.length})
            </button>
            <button
              onClick={() => setActiveTab('guides')}
              className={`flex-1 py-4 px-6 rounded-lg text-lg font-semibold transition-all ${
                activeTab === 'guides'
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-300'
              }`}
            >
              🎓 Guide Applications ({guideApplications.filter(g => g.status === 'pending').length} pending)
            </button>
            <button
              onClick={() => {
                setActiveTab('schools');
                // Also load schools when tab is clicked
                if (schools.length === 0) {
                  loadSchools();
                }
              }}
              className={`flex-1 py-4 px-6 rounded-lg text-lg font-semibold transition-all ${
                activeTab === 'schools'
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-300'
              }`}
            >
              🏫 Manage Schools ({schools.length})
            </button>
            <button
              onClick={() => {
                setActiveTab('reviews');
                if (reviews.length === 0) {
                  loadReviews();
                }
              }}
              className={`flex-1 py-4 px-6 rounded-lg text-lg font-semibold transition-all ${
                activeTab === 'reviews'
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-300'
              }`}
              data-testid="reviews-tab-button"
            >
              <Star className="w-5 h-5 inline mr-1" /> Reviews ({reviews.length})
            </button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="hidden">
            <TabsTrigger value="bookings">Bookings</TabsTrigger>
            <TabsTrigger value="tours">Tour Bookings</TabsTrigger>
            <TabsTrigger value="guides">Guides</TabsTrigger>
            <TabsTrigger value="schools">Schools</TabsTrigger>
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Total Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold">{bookings.length}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Confirmed</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-green-600">
                    {bookings.filter(b => b.status === 'confirmed').length}
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Total Students</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold">
                    {bookings.reduce((sum, b) => sum + parseInt(b.number_of_people || 1), 0)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Bookings Table */}
            <Card>
              <CardHeader>
                <CardTitle>All Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 font-semibold">Booking ID</th>
                        <th className="text-left p-3 font-semibold">Student Name</th>
                        <th className="text-left p-3 font-semibold">Email</th>
                        <th className="text-left p-3 font-semibold">Phone</th>
                        <th className="text-left p-3 font-semibold">School</th>
                        <th className="text-left p-3 font-semibold">Instructor</th>
                        <th className="text-left p-3 font-semibold">Class Date</th>
                        <th className="text-left p-3 font-semibold">Time</th>
                        <th className="text-left p-3 font-semibold">People</th>
                        <th className="text-left p-3 font-semibold">Status</th>
                        <th className="text-left p-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.length === 0 ? (
                        <tr>
                          <td colSpan="11" className="text-center p-8 text-gray-500">
                            No bookings yet
                          </td>
                        </tr>
                      ) : (
                        bookings.map((booking) => (
                          <tr key={booking.id} className="border-b hover:bg-gray-50">
                            <td className="p-3 text-sm font-mono">
                              {booking.id.substring(0, 8)}...
                            </td>
                            <td className="p-3 font-medium">{booking.student_name}</td>
                            <td className="p-3 text-sm">
                              <a 
                                href={`mailto:${booking.student_email}`}
                                className="text-blue-600 hover:underline"
                                title="Click to email student"
                              >
                                {booking.student_email}
                              </a>
                            </td>
                            <td className="p-3 text-sm">{booking.phone}</td>
                            <td className="p-3">{booking.school}</td>
                            <td className="p-3">{booking.instructor_name}</td>
                            <td className="p-3 whitespace-nowrap">{booking.time_slot_date}</td>
                            <td className="p-3 whitespace-nowrap">{booking.time_slot_time}</td>
                            <td className="p-3 text-center">{booking.number_of_people}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(booking.status)}`}>
                                {booking.status}
                              </span>
                            </td>
                            <td className="p-3">
                              <a
                                href={`mailto:${booking.student_email}?subject=Your Class Booking with ${booking.instructor_name}&body=Hi ${booking.student_name},%0D%0A%0D%0AThank you for booking a class with We.Tour!%0D%0A%0D%0AYour class details:%0D%0AInstructor: ${booking.instructor_name}%0D%0ADate: ${booking.time_slot_date}%0D%0ATime: ${booking.time_slot_time}%0D%0A%0D%0AZoom Link: [Add your Zoom link here]%0D%0A%0D%0ABest regards,%0D%0AThe We.Tour Team`}
                                className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Email Student
                              </a>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tour Bookings Tab */}
          <TabsContent value="tours">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <Card>
                <CardHeader><CardTitle className="text-lg">Total Tours</CardTitle></CardHeader>
                <CardContent><p className="text-4xl font-bold">{tourBookings.length}</p></CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-lg">Deposit Paid</CardTitle></CardHeader>
                <CardContent><p className="text-4xl font-bold text-green-600">{tourBookings.filter(b => b.payment_status === 'deposit_paid').length}</p></CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-lg">Fully Paid</CardTitle></CardHeader>
                <CardContent><p className="text-4xl font-bold text-blue-600">{tourBookings.filter(b => b.payment_status === 'fully_paid').length}</p></CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-lg">Refunded</CardTitle></CardHeader>
                <CardContent><p className="text-4xl font-bold text-red-600">{tourBookings.filter(b => b.payment_status === 'refunded').length}</p></CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Tour Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 font-semibold text-sm">Customer</th>
                        <th className="text-left p-3 font-semibold text-sm">Email</th>
                        <th className="text-left p-3 font-semibold text-sm">Guide</th>
                        <th className="text-left p-3 font-semibold text-sm">Tour Type</th>
                        <th className="text-left p-3 font-semibold text-sm">Date</th>
                        <th className="text-right p-3 font-semibold text-sm">Price</th>
                        <th className="text-center p-3 font-semibold text-sm">Payment</th>
                        <th className="text-center p-3 font-semibold text-sm">Card Saved</th>
                        <th className="text-center p-3 font-semibold text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tourBookings.length === 0 ? (
                        <tr><td colSpan="9" className="text-center p-8 text-gray-500">No tour bookings yet</td></tr>
                      ) : (
                        tourBookings.map((tb) => {
                          const price = parseFloat(tb.price || 0);
                          const deposit = Math.round(price * 0.1 * 100) / 100;
                          const remaining = Math.round((price - deposit) * 100) / 100;
                          const hasCard = tb.stripe_customer_id && tb.stripe_payment_method;
                          return (
                            <tr key={tb.id} className="border-b hover:bg-gray-50" data-testid={`tour-booking-${tb.id}`}>
                              <td className="p-3 font-medium text-sm">{tb.visitor_name}</td>
                              <td className="p-3 text-sm">
                                <a href={`mailto:${tb.visitor_email}`} className="text-blue-600 hover:underline">{tb.visitor_email}</a>
                              </td>
                              <td className="p-3 text-sm">{tb.guide_name}</td>
                              <td className="p-3 text-sm">{tb.tour_type}</td>
                              <td className="p-3 text-sm whitespace-nowrap">{tb.preferred_date}</td>
                              <td className="p-3 text-sm text-right font-medium">${price.toFixed(2)}</td>
                              <td className="p-3 text-center">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  tb.payment_status === 'fully_paid' ? 'bg-blue-100 text-blue-800' :
                                  tb.payment_status === 'deposit_paid' ? 'bg-green-100 text-green-800' :
                                  tb.payment_status === 'refunded' ? 'bg-red-100 text-red-800' :
                                  tb.payment_status === 'deposit_pending' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {tb.payment_status || 'none'}
                                </span>
                              </td>
                              <td className="p-3 text-center">
                                {hasCard ? (
                                  <span className="text-green-600 text-xs font-medium">Yes</span>
                                ) : (
                                  <span className="text-gray-400 text-xs">No</span>
                                )}
                              </td>
                              <td className="p-3 text-center space-y-1">
                                {tb.payment_status === 'deposit_paid' && (
                                  <div className="flex flex-col gap-1">
                                    {hasCard && (
                                      <button
                                        data-testid={`charge-btn-${tb.id}`}
                                        onClick={() => handleChargeBalance(tb.id, tb.tip_amount)}
                                        className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 w-full"
                                      >
                                        Charge Balance (${remaining.toFixed(2)})
                                      </button>
                                    )}
                                    <button
                                      data-testid={`refund-btn-${tb.id}`}
                                      onClick={() => handleRefundDeposit(tb.id)}
                                      className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 w-full"
                                    >
                                      Refund Deposit
                                    </button>
                                  </div>
                                )}
                                {tb.payment_status === 'fully_paid' && (
                                  <span className="text-xs text-gray-500">
                                    Paid in full{tb.tip_amount > 0 ? ` (tip: $${parseFloat(tb.tip_amount).toFixed(2)})` : ''}
                                  </span>
                                )}
                                {tb.payment_status === 'refunded' && (
                                  <span className="text-xs text-red-500">Refunded</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Guide Applications Tab */}
          <TabsContent value="guides">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Total Applications</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold">{guideApplications.length}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Pending</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-yellow-600">
                    {guideApplications.filter(g => g.status === 'pending').length}
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Approved</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-green-600">
                    {guideApplications.filter(g => g.status === 'approved').length}
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Rejected</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-red-600">
                    {guideApplications.filter(g => g.status === 'rejected').length}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Applications Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {guideApplications.length === 0 ? (
                <Card className="col-span-2">
                  <CardContent className="text-center p-12 text-gray-500">
                    No guide applications yet
                  </CardContent>
                </Card>
              ) : (
                guideApplications.map((application) => (
                  <Card key={application.id} className="overflow-hidden">
                    <CardHeader className="bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-4">
                          {application.photo_urls && application.photo_urls.length > 0 ? (
                            <img 
                              src={application.photo_urls[0]} 
                              alt={application.name}
                              className="w-16 h-16 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <CardTitle className="text-xl">{application.name}</CardTitle>
                            <p className="text-sm text-gray-600">{application.school}</p>
                            {application.academic_year && (
                              <p className="text-xs text-gray-500">{application.academic_year} • {application.major || 'No major'}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => editingGuideId === application.id ? setEditingGuideId(null) : startEditingGuide(application)}
                            className="text-xs"
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            {editingGuideId === application.id ? 'Cancel Edit' : 'Edit Guide'}
                          </Button>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(application.status)}`}>
                            {application.status}
                          </span>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4 space-y-4 max-h-[600px] overflow-y-auto">
                      {/* Edit Guide Form */}
                      {editingGuideId === application.id && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                          <h4 className="font-semibold text-blue-800 flex items-center">
                            <Edit className="w-4 h-4 mr-2" /> Edit Guide Information
                          </h4>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Name</Label>
                              <Input
                                value={editGuideData.name || ''}
                                onChange={(e) => handleEditGuideChange('name', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Email</Label>
                              <Input
                                value={editGuideData.email || ''}
                                onChange={(e) => handleEditGuideChange('email', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Phone</Label>
                              <Input
                                value={editGuideData.phone || ''}
                                onChange={(e) => handleEditGuideChange('phone', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">School</Label>
                              <Input
                                value={editGuideData.school || ''}
                                onChange={(e) => handleEditGuideChange('school', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Year</Label>
                              <Input
                                value={editGuideData.academic_year || ''}
                                onChange={(e) => handleEditGuideChange('academic_year', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Major</Label>
                              <Input
                                value={editGuideData.major || ''}
                                onChange={(e) => handleEditGuideChange('major', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Minor</Label>
                              <Input
                                value={editGuideData.minor || ''}
                                onChange={(e) => handleEditGuideChange('minor', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Hometown</Label>
                              <Input
                                value={editGuideData.hometown || ''}
                                onChange={(e) => handleEditGuideChange('hometown', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">High School</Label>
                              <Input
                                value={editGuideData.high_school || ''}
                                onChange={(e) => handleEditGuideChange('high_school', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Meeting Point</Label>
                              <Input
                                value={editGuideData.meeting_point || ''}
                                onChange={(e) => handleEditGuideChange('meeting_point', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Gender</Label>
                              <select
                                value={editGuideData.gender || ''}
                                onChange={(e) => handleEditGuideChange('gender', e.target.value)}
                                className="w-full p-2 border rounded text-sm"
                                data-testid="edit-guide-gender"
                              >
                                <option value="">Select</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Non-binary">Non-binary</option>
                                <option value="Prefer not to say">Prefer not to say</option>
                              </select>
                            </div>
                          </div>
                          
                          <div>
                            <Label className="text-xs">Bio</Label>
                            <textarea
                              value={editGuideData.bio || ''}
                              onChange={(e) => handleEditGuideChange('bio', e.target.value)}
                              className="w-full p-2 border rounded text-sm min-h-[60px]"
                            />
                          </div>
                          
                          <div>
                            <Label className="text-xs">Tour Description</Label>
                            <textarea
                              value={editGuideData.tour_description || ''}
                              onChange={(e) => handleEditGuideChange('tour_description', e.target.value)}
                              className="w-full p-2 border rounded text-sm min-h-[60px]"
                            />
                          </div>
                          
                          <div>
                            <Label className="text-xs">Availability</Label>
                            <textarea
                              value={editGuideData.availability || ''}
                              onChange={(e) => handleEditGuideChange('availability', e.target.value)}
                              className="w-full p-2 border rounded text-sm min-h-[40px]"
                            />
                          </div>
                          
                          <h5 className="font-medium text-sm text-blue-700 mt-4">Personal Questions</h5>
                          
                          <div>
                            <Label className="text-xs">College Experience</Label>
                            <textarea
                              value={editGuideData.college_experience || ''}
                              onChange={(e) => handleEditGuideChange('college_experience', e.target.value)}
                              className="w-full p-2 border rounded text-sm min-h-[80px]"
                            />
                          </div>
                          
                          <div>
                            <Label className="text-xs">Tip for Students</Label>
                            <textarea
                              value={editGuideData.tip_for_students || ''}
                              onChange={(e) => handleEditGuideChange('tip_for_students', e.target.value)}
                              className="w-full p-2 border rounded text-sm min-h-[60px]"
                            />
                          </div>
                          
                          <div>
                            <Label className="text-xs">Favorite Class</Label>
                            <Input
                              value={editGuideData.favorite_class || ''}
                              onChange={(e) => handleEditGuideChange('favorite_class', e.target.value)}
                              className="text-sm"
                            />
                          </div>
                          
                          <div>
                            <Label className="text-xs">Career Goals</Label>
                            <textarea
                              value={editGuideData.career_goals || ''}
                              onChange={(e) => handleEditGuideChange('career_goals', e.target.value)}
                              className="w-full p-2 border rounded text-sm min-h-[60px]"
                            />
                          </div>
                          
                          <div className="flex gap-2 pt-2">
                            <Button
                              onClick={() => handleSaveGuide(application.id)}
                              disabled={savingGuide}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <Save className="w-4 h-4 mr-1" />
                              {savingGuide ? 'Saving...' : 'Save Changes'}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => setEditingGuideId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Photo Gallery */}
                      {application.photo_urls && application.photo_urls.length > 0 && (
                        <div className="border-b pb-4">
                          <div className="flex justify-between items-center mb-2">
                            <p className="text-sm font-medium">Photos ({application.photo_urls.length}):</p>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingPhotosId(editingPhotosId === application.id ? null : application.id);
                                setNewPhotos([]);
                              }}
                              className="text-xs"
                            >
                              <Camera className="w-3 h-3 mr-1" />
                              {editingPhotosId === application.id ? 'Cancel' : 'Add Photos'}
                            </Button>
                          </div>
                          
                          {/* Add Photos Section */}
                          {editingPhotosId === application.id && (
                            <div className="bg-blue-50 p-3 rounded-lg mb-3">
                              <p className="text-xs text-blue-700 mb-2">Select photos to add (will keep existing photos):</p>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handlePhotoFileChange}
                                className="text-xs mb-2"
                              />
                              {newPhotos.length > 0 && (
                                <div className="flex gap-2 flex-wrap mb-2">
                                  {newPhotos.map((file, idx) => (
                                    <div key={idx} className="relative">
                                      <img 
                                        src={URL.createObjectURL(file)}
                                        alt={`New photo ${idx + 1}`}
                                        className="w-16 h-16 rounded object-cover border-2 border-blue-400"
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                              <Button
                                size="sm"
                                onClick={() => handleAddPhotos(application.id)}
                                disabled={uploadingPhotos || newPhotos.length < 1}
                                className="bg-blue-600 text-white text-xs"
                              >
                                {uploadingPhotos ? 'Uploading...' : `Add ${newPhotos.length} Photo(s)`}
                              </Button>
                            </div>
                          )}
                          
                          {/* Current Photos */}
                          <div className="flex gap-2 flex-wrap">
                            {application.photo_urls.map((url, idx) => (
                              <div key={idx} className="relative group">
                                <img 
                                  src={url}
                                  alt={`${application.name} photo ${idx + 1}`}
                                  className={`w-20 h-20 rounded-lg object-cover cursor-pointer hover:opacity-80 ${idx === 0 ? 'ring-2 ring-green-500' : 'border'}`}
                                  onClick={() => window.open(url, '_blank')}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23fecaca" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%23991b1b" font-size="8">Broken</text></svg>';
                                  }}
                                />
                                {/* Profile badge for first photo */}
                                {idx === 0 && (
                                  <span className="absolute -top-1 -left-1 bg-green-500 text-white text-[8px] px-1 rounded">
                                    Profile
                                  </span>
                                )}
                                {/* Delete button - top right */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeletePhoto(application.id, idx);
                                  }}
                                  disabled={deletingPhoto === `${application.id}-${idx}`}
                                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                  title="Delete photo"
                                >
                                  {deletingPhoto === `${application.id}-${idx}` ? '...' : '×'}
                                </button>
                                {/* Set as Profile button for non-first photos */}
                                {idx !== 0 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSetProfilePicture(application.id, idx);
                                    }}
                                    disabled={settingProfilePic === `${application.id}-${idx}`}
                                    className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[8px] py-1 opacity-0 group-hover:opacity-100 transition-opacity rounded-b-lg"
                                  >
                                    {settingProfilePic === `${application.id}-${idx}` ? '...' : 'Set as Profile'}
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Hover: Delete (×) or Set as Profile. First photo = Profile picture.</p>
                          
                          {/* Photo Position Selector */}
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <ImageIcon className="w-4 h-4 text-gray-600" />
                              <p className="text-sm font-medium">Photo Position:</p>
                            </div>
                            <div className="flex gap-2">
                              {['top', 'center', 'bottom', 'full'].map((pos) => (
                                <button
                                  key={pos}
                                  onClick={() => handlePhotoPositionChange(application.id, pos)}
                                  disabled={savingPhotoPosition === application.id}
                                  className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                                    (application.photo_position || 'center') === pos
                                      ? 'bg-black text-white border-black'
                                      : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                                  }`}
                                >
                                  {pos.charAt(0).toUpperCase() + pos.slice(1)}
                                </button>
                              ))}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">"Full" shows entire photo without cropping. Use "Top" for full-body photos.</p>
                          </div>
                        </div>
                      )}
                      
                      {/* No photos - show add button */}
                      {(!application.photo_urls || application.photo_urls.length === 0) && (
                        <div className="border-b pb-4">
                          <div className="flex justify-between items-center mb-2">
                            <p className="text-sm font-medium">Photos (0):</p>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingPhotosId(editingPhotosId === application.id ? null : application.id);
                                setNewPhotos([]);
                              }}
                              className="text-xs"
                            >
                              <Camera className="w-3 h-3 mr-1" />
                              {editingPhotosId === application.id ? 'Cancel' : 'Add Photos'}
                            </Button>
                          </div>
                          
                          {editingPhotosId === application.id && (
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <p className="text-xs text-blue-700 mb-2">Select photos to add:</p>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handlePhotoFileChange}
                                className="text-xs mb-2"
                              />
                              {newPhotos.length > 0 && (
                                <div className="flex gap-2 flex-wrap mb-2">
                                  {newPhotos.map((file, idx) => (
                                    <div key={idx} className="relative">
                                      <img 
                                        src={URL.createObjectURL(file)}
                                        alt={`New photo ${idx + 1}`}
                                        className="w-16 h-16 rounded object-cover border-2 border-blue-400"
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                              <Button
                                size="sm"
                                onClick={() => handleAddPhotos(application.id)}
                                disabled={uploadingPhotos || newPhotos.length < 1}
                                className="bg-blue-600 text-white text-xs"
                              >
                                {uploadingPhotos ? 'Uploading...' : `Add ${newPhotos.length} Photo(s)`}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Contact Info */}
                      <div className="grid grid-cols-2 gap-4 text-sm border-b pb-4">
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 mr-2 text-gray-400" />
                          <a href={`mailto:${application.email}`} className="text-blue-600 hover:underline truncate">
                            {application.email}
                          </a>
                        </div>
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 mr-2 text-gray-400" />
                          <span>{application.phone}</span>
                        </div>
                        <div className="flex items-start col-span-2">
                          <Calendar className="w-4 h-4 mr-2 mt-1 text-gray-400" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">Availability:</span>
                              <button
                                onClick={() => {
                                  if (editingAvailabilityId === application.id) {
                                    setEditingAvailabilityId(null);
                                    setAvailabilityDisplay('');
                                  } else {
                                    setEditingAvailabilityId(application.id);
                                    setAvailabilityDisplay(application.availability_display || '');
                                  }
                                }}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                {editingAvailabilityId === application.id ? 'Cancel' : 'Edit Display'}
                              </button>
                            </div>
                            
                            {/* Show current availability */}
                            <p className="text-xs text-gray-600 mt-1">{application.availability}</p>
                            
                            {/* Show custom display if set */}
                            {application.availability_display && editingAvailabilityId !== application.id && (
                              <p className="text-xs font-medium text-green-700 mt-1 bg-green-50 p-1 rounded">
                                Display: {application.availability_display}
                              </p>
                            )}
                            
                            {/* Edit form */}
                            {editingAvailabilityId === application.id && (
                              <div className="mt-2 bg-blue-50 p-2 rounded">
                                <p className="text-xs text-blue-700 mb-1">Set a simplified availability display:</p>
                                <input
                                  type="text"
                                  value={availabilityDisplay}
                                  onChange={(e) => setAvailabilityDisplay(e.target.value)}
                                  placeholder="e.g., Weekdays 2-6pm, Weekends 10am-4pm"
                                  className="w-full text-xs border rounded p-2 mb-2"
                                />
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveAvailabilityDisplay(application.id)}
                                  disabled={savingAvailability}
                                  className="bg-blue-600 text-white text-xs"
                                >
                                  {savingAvailability ? 'Saving...' : 'Save Display'}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* About Me Section */}
                      <div className="border-b pb-4">
                        <h4 className="font-semibold text-sm mb-2 text-gray-800">📋 About Me</h4>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          {application.gender && (
                            <div className="bg-gray-50 p-2 rounded">
                              <span className="text-gray-500">Gender:</span>
                              <p className="font-medium">{application.gender}</p>
                            </div>
                          )}
                          {application.age && (
                            <div className="bg-gray-50 p-2 rounded">
                              <span className="text-gray-500">Age:</span>
                              <p className="font-medium">{application.age}</p>
                            </div>
                          )}
                          {application.academic_year && (
                            <div className="bg-gray-50 p-2 rounded">
                              <span className="text-gray-500">Year:</span>
                              <p className="font-medium">{application.academic_year}</p>
                            </div>
                          )}
                          {application.admission_type && (
                            <div className="bg-gray-50 p-2 rounded">
                              <span className="text-gray-500">Admission:</span>
                              <p className="font-medium">{application.admission_type}</p>
                            </div>
                          )}
                          {application.personality_type && (
                            <div className="bg-gray-50 p-2 rounded">
                              <span className="text-gray-500">Personality:</span>
                              <p className="font-medium">{application.personality_type}</p>
                            </div>
                          )}
                          {application.college_rating && (
                            <div className="bg-gray-50 p-2 rounded">
                              <span className="text-gray-500">College Rating:</span>
                              <p className="font-medium">{application.college_rating}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Academic Info */}
                      <div className="border-b pb-4">
                        <h4 className="font-semibold text-sm mb-2 text-gray-800">🎓 Academic Info</h4>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {application.hometown && (
                            <div className="bg-blue-50 p-2 rounded">
                              <span className="text-gray-500">Hometown:</span>
                              <p className="font-medium">{application.hometown}</p>
                            </div>
                          )}
                          {application.major && (
                            <div className="bg-blue-50 p-2 rounded">
                              <span className="text-gray-500">Major:</span>
                              <p className="font-medium">{application.major}</p>
                            </div>
                          )}
                          {application.minor && (
                            <div className="bg-blue-50 p-2 rounded">
                              <span className="text-gray-500">Minor:</span>
                              <p className="font-medium">{application.minor}</p>
                            </div>
                          )}
                          {application.high_school && (
                            <div className="bg-blue-50 p-2 rounded">
                              <span className="text-gray-500">High School:</span>
                              <p className="font-medium">{application.high_school}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Extracurriculars */}
                      {application.extracurriculars && application.extracurriculars.length > 0 && (
                        <div className="border-b pb-4">
                          <h4 className="font-semibold text-sm mb-2 text-gray-800">🏃 Extracurriculars</h4>
                          <div className="flex flex-wrap gap-1">
                            {application.extracurriculars.map((activity, idx) => (
                              <span key={idx} className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                                {activity}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Clubs */}
                      {application.clubs && (
                        <div className="border-b pb-4">
                          <h4 className="font-semibold text-sm mb-2 text-gray-800">🎭 Clubs & Organizations</h4>
                          <p className="text-sm text-gray-600">{application.clubs}</p>
                        </div>
                      )}

                      {/* Housing */}
                      {application.housing_experience && application.housing_experience.length > 0 && (
                        <div className="border-b pb-4">
                          <h4 className="font-semibold text-sm mb-2 text-gray-800">🏠 Housing Experience</h4>
                          <div className="flex flex-wrap gap-1">
                            {application.housing_experience.map((housing, idx) => (
                              <span key={idx} className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">
                                {housing}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Bio */}
                      <div className="border-b pb-4">
                        <h4 className="font-semibold text-sm mb-1 flex items-center">
                          <User className="w-4 h-4 mr-2" /> Bio
                        </h4>
                        <p className="text-sm text-gray-600">{application.bio}</p>
                      </div>

                      {/* Personal Questions */}
                      {(application.college_experience || application.tip_for_students || application.favorite_class || application.career_goals) && (
                        <div className="border-b pb-4 space-y-3">
                          <h4 className="font-semibold text-sm text-gray-800">💬 Personal Questions</h4>
                          
                          {application.college_experience && (
                            <div className="bg-yellow-50 p-3 rounded text-sm">
                              <p className="text-xs text-gray-500 font-medium mb-1">College Experience:</p>
                              <p className="text-gray-700">{application.college_experience}</p>
                            </div>
                          )}
                          
                          {application.tip_for_students && (
                            <div className="bg-yellow-50 p-3 rounded text-sm">
                              <p className="text-xs text-gray-500 font-medium mb-1">Tip for Students:</p>
                              <p className="text-gray-700">{application.tip_for_students}</p>
                            </div>
                          )}
                          
                          {application.favorite_class && (
                            <div className="bg-yellow-50 p-3 rounded text-sm">
                              <p className="text-xs text-gray-500 font-medium mb-1">Favorite Class:</p>
                              <p className="text-gray-700">{application.favorite_class}</p>
                            </div>
                          )}
                          
                          {application.career_goals && (
                            <div className="bg-yellow-50 p-3 rounded text-sm">
                              <p className="text-xs text-gray-500 font-medium mb-1">Career Goals:</p>
                              <p className="text-gray-700">{application.career_goals}</p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Tour Info */}
                      <div className="border-b pb-4">
                        <h4 className="font-semibold text-sm mb-1 flex items-center">
                          <FileText className="w-4 h-4 mr-2" /> Tour Description
                        </h4>
                        <p className="text-sm text-gray-600">{application.tour_description}</p>
                        
                        {application.meeting_point && (
                          <div className="mt-2 bg-gray-50 p-2 rounded">
                            <p className="text-xs text-gray-500">Meeting Point:</p>
                            <p className="text-sm font-medium">{application.meeting_point}</p>
                          </div>
                        )}
                      </div>

                      {/* Pricing Section */}
                      <div className="border-b pb-4">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-semibold text-sm flex items-center">
                            <DollarSign className="w-4 h-4 mr-2" /> Pricing
                          </h4>
                          <button
                            onClick={() => {
                              if (editingPricingId === application.id) {
                                setEditingPricingId(null);
                                setPricingCampus('');
                                setPricingVideo('');
                                setCalculatedPrices({ campus: null, video: null });
                              } else {
                                setEditingPricingId(application.id);
                                setPricingCampus(application.desired_earnings_campus || '');
                                setPricingVideo(application.desired_earnings_video || '');
                                setCalculatedPrices({
                                  campus: application.price_campus_tour || null,
                                  video: application.price_video_chat || null
                                });
                              }
                            }}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            {editingPricingId === application.id ? 'Cancel' : 'Set Pricing'}
                          </button>
                        </div>

                        {/* Current Pricing Display */}
                        {(application.price_campus_tour || application.price_video_chat) && editingPricingId !== application.id && (
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="bg-green-50 p-2 rounded border border-green-200">
                              <p className="text-gray-500">Campus Tour</p>
                              {application.price_campus_tour ? (
                                <>
                                  <p className="font-bold text-green-700 text-lg">${application.price_campus_tour}</p>
                                  <p className="text-[10px] text-gray-500">Guide gets: ${application.desired_earnings_campus}</p>
                                </>
                              ) : (
                                <p className="text-gray-400">Not set</p>
                              )}
                            </div>
                            <div className="bg-blue-50 p-2 rounded border border-blue-200">
                              <p className="text-gray-500">Video Tour</p>
                              {application.price_video_chat ? (
                                <>
                                  <p className="font-bold text-blue-700 text-lg">${application.price_video_chat}</p>
                                  <p className="text-[10px] text-gray-500">Guide gets: ${application.desired_earnings_video}</p>
                                </>
                              ) : (
                                <p className="text-gray-400">Not set</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* No pricing set message */}
                        {!application.price_campus_tour && !application.price_video_chat && editingPricingId !== application.id && (
                          <p className="text-xs text-gray-400 italic">No pricing set. Click "Set Pricing" to add.</p>
                        )}

                        {/* Pricing Edit Form */}
                        {editingPricingId === application.id && (
                          <div className="bg-gradient-to-r from-green-50 to-blue-50 p-3 rounded-lg border border-gray-200">
                            {/* Pricing Mode Selection */}
                            <div className="mb-4 p-2 bg-white rounded border">
                              <p className="text-xs font-medium text-gray-700 mb-2">Fee Type:</p>
                              <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name="pricingMode"
                                    value="percentage"
                                    checked={pricingMode === 'percentage'}
                                    onChange={() => setPricingMode('percentage')}
                                    className="accent-blue-600"
                                  />
                                  <span className="text-sm">20% Commission</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name="pricingMode"
                                    value="fixed"
                                    checked={pricingMode === 'fixed'}
                                    onChange={() => setPricingMode('fixed')}
                                    className="accent-blue-600"
                                  />
                                  <span className="text-sm">Fixed Fee</span>
                                </label>
                                {pricingMode === 'fixed' && (
                                  <div className="flex items-center gap-1">
                                    <span className="text-sm">$</span>
                                    <input
                                      type="number"
                                      value={fixedFee}
                                      onChange={(e) => setFixedFee(parseFloat(e.target.value) || 0)}
                                      className="w-16 text-sm border rounded p-1"
                                      min="0"
                                    />
                                    <span className="text-xs text-gray-500">per tour</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <p className="text-xs text-gray-600 mb-3">
                              {pricingMode === 'percentage' 
                                ? "Enter what the guide wants to earn. Customer price is calculated automatically (Guide gets 80%, WeTours gets 20%)."
                                : `Enter what the guide wants to earn. Customer pays guide earnings + $${fixedFee} fixed fee.`
                              }
                            </p>
                            
                            <div className="grid grid-cols-2 gap-3">
                              {/* Campus Tour Pricing */}
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Campus Tour</label>
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-500">$</span>
                                  <input
                                    type="number"
                                    value={pricingCampus}
                                    onChange={(e) => handlePricingInputChange('campus', e.target.value)}
                                    placeholder="Guide earns"
                                    className="w-full text-sm border rounded p-2"
                                    min="0"
                                    step="1"
                                  />
                                </div>
                                {calculatedPrices.campus && (
                                  <div className="bg-white p-2 rounded text-xs border">
                                    <p className="text-green-700">Customer pays: <span className="font-bold">${calculatedPrices.campus}</span></p>
                                    <p className="text-gray-500">Guide gets: ${pricingCampus}</p>
                                    <p className="text-gray-500">WeTours: ${(calculatedPrices.campus - pricingCampus).toFixed(2)}</p>
                                  </div>
                                )}
                              </div>

                              {/* Video Tour Pricing */}
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Video Tour</label>
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-500">$</span>
                                  <input
                                    type="number"
                                    value={pricingVideo}
                                    onChange={(e) => handlePricingInputChange('video', e.target.value)}
                                    placeholder="Guide earns"
                                    className="w-full text-sm border rounded p-2"
                                    min="0"
                                    step="1"
                                  />
                                </div>
                                {calculatedPrices.video && (
                                  <div className="bg-white p-2 rounded text-xs border">
                                    <p className="text-blue-700">Customer pays: <span className="font-bold">${calculatedPrices.video}</span></p>
                                    <p className="text-gray-500">Guide gets: ${pricingVideo}</p>
                                    <p className="text-gray-500">WeTours: ${(calculatedPrices.video - pricingVideo).toFixed(2)}</p>
                                  </div>
                                )}
                              </div>
                            </div>

                            <Button
                              size="sm"
                              onClick={() => handleSavePricing(application.id)}
                              disabled={savingPricing || (!pricingCampus && !pricingVideo)}
                              className="mt-3 bg-black text-white text-xs w-full"
                            >
                              {savingPricing ? 'Saving...' : 'Save Pricing'}
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-2">
                        {application.status === 'pending' && (
                          <>
                            <Button
                              onClick={() => handleApproveGuide(application.id)}
                              className="flex-1 bg-green-600 text-white hover:bg-green-700"
                              size="sm"
                            >
                              <Check className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button
                              onClick={() => handleRejectGuide(application.id)}
                              variant="outline"
                              className="flex-1 border-red-600 text-red-600 hover:bg-red-50"
                              size="sm"
                            >
                              <X className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                        {application.status === 'rejected' && (
                          <Button
                            onClick={() => handleApproveGuide(application.id)}
                            className="flex-1 bg-green-600 text-white hover:bg-green-700"
                            size="sm"
                          >
                            <Check className="w-4 h-4 mr-1" /> Approve
                          </Button>
                        )}
                        {application.status === 'approved' && (
                          <Button
                            onClick={() => handleRejectGuide(application.id)}
                            variant="outline"
                            className="flex-1 border-yellow-600 text-yellow-600 hover:bg-yellow-50"
                            size="sm"
                          >
                            Revoke Approval
                          </Button>
                        )}
                        <Button
                          onClick={() => handleDeleteGuide(application.id)}
                          variant="outline"
                          className="border-gray-300 text-gray-600 hover:bg-gray-50"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Schools Tab */}
          <TabsContent value="schools">
            <div className="space-y-6">
              {/* Header with Add Button */}
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Manage Schools</h2>
                  <p className="text-gray-600">Control which schools appear in the Explore Schools section</p>
                </div>
                <Button 
                  onClick={() => setShowAddSchool(!showAddSchool)}
                  className="bg-black text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add School
                </Button>
              </div>

              {/* Add School Form */}
              {showAddSchool && (
                <Card className="border-2 border-dashed border-gray-300">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-4">Add New School</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="schoolName">School Name *</Label>
                        <Input
                          id="schoolName"
                          value={newSchool.name}
                          onChange={(e) => setNewSchool({...newSchool, name: e.target.value})}
                          placeholder="e.g., Yale University"
                        />
                      </div>
                      <div>
                        <Label htmlFor="schoolCity">City *</Label>
                        <Input
                          id="schoolCity"
                          value={newSchool.city}
                          onChange={(e) => setNewSchool({...newSchool, city: e.target.value})}
                          placeholder="e.g., New Haven, CT"
                        />
                      </div>
                      <div>
                        <Label htmlFor="schoolImage">Image URL (optional)</Label>
                        <Input
                          id="schoolImage"
                          value={newSchool.image}
                          onChange={(e) => setNewSchool({...newSchool, image: e.target.value})}
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button 
                        onClick={handleAddSchool}
                        disabled={addingSchool}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {addingSchool ? 'Adding...' : 'Add School'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setShowAddSchool(false);
                          setNewSchool({ name: '', city: '', image: '' });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Schools List */}
              {loadingSchools ? (
                <div className="text-center py-12">Loading schools...</div>
              ) : (
                <div className="grid gap-4">
                  {schools.map((school) => (
                    <Card 
                      key={school.id}
                      className={`${!school.is_visible ? 'opacity-60 bg-gray-50' : ''}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            {/* School Image */}
                            <div className="w-20 h-14 rounded overflow-hidden bg-gray-200 flex-shrink-0">
                              {school.image ? (
                                <img 
                                  src={school.image} 
                                  alt={school.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <School className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                            </div>
                            
                            {/* School Info */}
                            <div>
                              <h3 className="font-semibold text-lg">{school.name}</h3>
                              <p className="text-sm text-gray-600">{school.city}</p>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            {/* Reorder Buttons */}
                            <div className="flex flex-col gap-0.5 mr-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReorderSchool(school.id, 'up')}
                                className="h-6 w-6 p-0 hover:bg-gray-200"
                                data-testid={`school-up-${school.id}`}
                              >
                                <ChevronUp className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReorderSchool(school.id, 'down')}
                                className="h-6 w-6 p-0 hover:bg-gray-200"
                                data-testid={`school-down-${school.id}`}
                              >
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                            </div>

                            {/* Visibility Status */}
                            <span className={`text-xs px-2 py-1 rounded ${
                              school.is_visible 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-200 text-gray-600'
                            }`}>
                              {school.is_visible ? 'Visible' : 'Hidden'}
                            </span>

                            {/* Toggle Visibility Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleSchoolVisibility(school.id)}
                              className={school.is_visible ? 'text-gray-600' : 'text-green-600'}
                            >
                              {school.is_visible ? (
                                <>
                                  <EyeOff className="w-4 h-4 mr-1" />
                                  Hide
                                </>
                              ) : (
                                <>
                                  <Eye className="w-4 h-4 mr-1" />
                                  Show
                                </>
                              )}
                            </Button>

                            {/* Edit Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => editingSchoolId === school.id ? setEditingSchoolId(null) : startEditingSchool(school)}
                              className="text-blue-600 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              {editingSchoolId === school.id ? 'Cancel' : 'Edit'}
                            </Button>

                            {/* Delete Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteSchool(school.id, school.name)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Edit School Form */}
                        {editingSchoolId === school.id && (
                          <div className="mt-4 pt-4 border-t bg-blue-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                              <Edit className="w-4 h-4 mr-2" /> Edit School
                            </h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div>
                                <Label className="text-xs">School Name</Label>
                                <Input
                                  value={editSchoolData.name}
                                  onChange={(e) => setEditSchoolData(prev => ({ ...prev, name: e.target.value }))}
                                  className="text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">City</Label>
                                <Input
                                  value={editSchoolData.city}
                                  onChange={(e) => setEditSchoolData(prev => ({ ...prev, city: e.target.value }))}
                                  className="text-sm"
                                />
                              </div>
                            </div>
                            
                            <div className="mb-4">
                              <Label className="text-xs">School Image</Label>
                              <div className="flex items-center gap-4 mt-2">
                                {editSchoolData.image && (
                                  <img 
                                    src={editSchoolData.image} 
                                    alt="Preview" 
                                    className="w-24 h-16 object-cover rounded"
                                  />
                                )}
                                <div className="flex-1">
                                  <Input
                                    value={editSchoolData.image}
                                    onChange={(e) => setEditSchoolData(prev => ({ ...prev, image: e.target.value }))}
                                    placeholder="Image URL"
                                    className="text-sm mb-2"
                                  />
                                  <p className="text-xs text-gray-500">Enter an image URL or upload below</p>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleSchoolImageUpload}
                                    className="text-xs mt-1"
                                    ref={schoolImageInputRef}
                                  />
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleSaveSchool(school.id)}
                                disabled={savingSchool}
                                className="bg-blue-600 hover:bg-blue-700"
                                size="sm"
                              >
                                <Save className="w-4 h-4 mr-1" />
                                {savingSchool ? 'Saving...' : 'Save Changes'}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditingSchoolId(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            {/* Reviews Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{reviews.length}</p>
                  <p className="text-sm text-gray-500">Total Reviews</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{reviews.filter(r => r.is_visible).length}</p>
                  <p className="text-sm text-gray-500">Visible</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-600">{reviews.filter(r => !r.is_visible).length}</p>
                  <p className="text-sm text-gray-500">Hidden</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{reviews.filter(r => r.is_verified).length}</p>
                  <p className="text-sm text-gray-500">Verified</p>
                </CardContent>
              </Card>
            </div>

            {loadingReviews ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
                <p className="mt-4 text-gray-500">Loading reviews...</p>
              </div>
            ) : reviews.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No reviews yet</p>
                  <p className="text-gray-400 text-sm mt-1">Reviews will appear here when customers submit them</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {reviews.map(review => {
                  const guideName = guideApplications.find(g => g.id === review.guide_id)?.name || 'Unknown Guide';
                  return (
                    <Card key={review.id} className={`${!review.is_visible ? 'opacity-60 border-red-200' : ''}`} data-testid={`review-card-${review.id}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {editingReviewId === review.id ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={editReviewerName}
                                    onChange={(e) => setEditReviewerName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveReviewerName(review.id); if (e.key === 'Escape') setEditingReviewId(null); }}
                                    className="border rounded px-2 py-1 text-sm font-semibold w-40"
                                    autoFocus
                                    data-testid={`edit-name-input-${review.id}`}
                                  />
                                  <button onClick={() => handleSaveReviewerName(review.id)} className="text-green-600 hover:text-green-700 text-xs font-medium">Save</button>
                                  <button onClick={() => setEditingReviewId(null)} className="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
                                </div>
                              ) : (
                                <span
                                  className="font-semibold text-lg cursor-pointer hover:text-blue-600 group flex items-center gap-1"
                                  onClick={() => { setEditingReviewId(review.id); setEditReviewerName(review.reviewer_name || ''); }}
                                  title="Click to edit name"
                                  data-testid={`reviewer-name-${review.id}`}
                                >
                                  {review.reviewer_name}
                                  <Pencil className="w-3 h-3 text-gray-300 group-hover:text-blue-500" />
                                </span>
                              )}
                              <div className="flex">
                                {[1,2,3,4,5].map(star => (
                                  <Star key={star} className={`w-4 h-4 ${star <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                                ))}
                              </div>
                              {review.is_verified && (
                                <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <ShieldCheck className="w-3 h-3" /> Verified
                                </span>
                              )}
                              {!review.is_visible && (
                                <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">Hidden</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mb-1">
                              For: <span className="font-medium text-gray-700">{guideName}</span>
                              {review.tour_type && <> &middot; {review.tour_type}</>}
                              {review.tour_date && <> &middot; {review.tour_date}</>}
                            </p>
                            {review.title && <p className="font-medium mb-1">{review.title}</p>}
                            <p className="text-gray-700">{review.comment}</p>
                            <p className="text-xs text-gray-400 mt-2">{new Date(review.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="flex gap-2 ml-4 flex-shrink-0">
                            {!review.is_verified && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleVerifyReview(review.id)}
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                data-testid={`verify-review-${review.id}`}
                              >
                                <ShieldCheck className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleReviewVisibility(review.id)}
                              className={review.is_visible ? 'text-yellow-600 border-yellow-200 hover:bg-yellow-50' : 'text-green-600 border-green-200 hover:bg-green-50'}
                              data-testid={`toggle-visibility-${review.id}`}
                            >
                              {review.is_visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteReview(review.id)}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              data-testid={`delete-review-${review.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
